import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import { useCallback, useState } from 'react';

import type { ButtonProps } from '@mui/material/Button';
import type { ReactNode } from 'react';

export type ConfirmationPopoverProps = {
	children: ReactNode;
	visible: { anchorEl: HTMLElement } | false;
	loading?: boolean;
	confirmLabel?: string;
	cancelLabel?: string;
	/** The confirm button's colour; defaults to error, for confirming something destructive. */
	confirmColor?: ButtonProps['color'];
	onConfirm: () => void | Promise<void>;
	onCancel: () => void;
};

export function ConfirmationPopover({
	children,
	visible,
	loading = false,
	confirmLabel = 'Yes',
	cancelLabel = 'No',
	confirmColor = 'error',
	onConfirm,
	onCancel,
}: ConfirmationPopoverProps) {
	const [confirmInFlight, setConfirmInFlight] = useState(false);
	const handleCancel = useCallback(() => {
		setConfirmInFlight(false);
		onCancel();
	}, [onCancel]);

	const handleConfirm = useCallback(() => {
		try {
			const result = onConfirm();
			if (result instanceof Promise) {
				setConfirmInFlight(true);
				void result.finally(() => {
					setConfirmInFlight(false);
				});
			}
		} catch {
			setConfirmInFlight(false);
		}
	}, [onConfirm]);

	const confirmLoading = loading || confirmInFlight;

	return (
		<Popover
			anchorEl={visible === false ? undefined : visible.anchorEl}
			anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
			transformOrigin={{ horizontal: 'right', vertical: 'top' }}
			open={visible !== false}
			// A question asked beside the control that raised it has no reason to freeze the page, and the
			// lock is the one thing here that can move what is behind it: it swaps the scroll container's
			// overflow and compensates with padding, which shifts the layout by a scrollbar's width wherever
			// that container is the one being scrolled.
			disableScrollLock
			onClose={() => handleCancel()}
		>
			{visible !== false && (
				<div className='flex flex-col gap-2 px-3 py-2'>
					<div className='my-1'>{children}</div>

					<div className='mx-0.5 grid grid-cols-2 gap-3'>
						<Button variant='outlined' onClick={handleCancel}>
							<span>{cancelLabel}</span>
						</Button>
						<Button color={confirmColor} variant='contained' autoFocus loading={confirmLoading} onClick={handleConfirm}>
							<span>{confirmLabel}</span>
						</Button>
					</div>
				</div>
			)}
		</Popover>
	);
}
